import type { Vacature, PagedResponse } from "../api/api";

// Practice mode for the help tours ("Oefenen"): while active, an
// "Oefenvacature" exists only in this browser. The vacature functions in
// api/api.ts list, read, update and delete it here instead of calling the
// backend, so practising never creates activity logs, alerts, emails or a
// Google Sheets row. Practice vacatures have negative IDs, which the
// backend never hands out.

export const PRACTICE_ID = -1;

let active = false;
// The "Vacature toevoegen" practice tour: a vacature created while this is
// set becomes a practice vacature too.
let captureCreates = false;
let vacatures: Vacature[] = [];
// The last known state of every practice vacature, so a late call for one
// (e.g. an undo toast clicked after the tour ended) still resolves without
// reaching the backend.
const lastKnown = new Map<number, Vacature>();
let nextCreatedId = PRACTICE_ID - 1;

export function isPracticeId(id: number): boolean {
  return id < 0;
}

export function isPracticeActive(): boolean {
  return active;
}

export function practiceCapturesCreates(): boolean {
  return active && captureCreates;
}

/** Starts practice mode. With `vacature`, the Oefenvacature is listed first in every vacature list. */
export function startPractice(opts: { vacature?: Vacature; captureCreates?: boolean }): void {
  active = true;
  captureCreates = !!opts.captureCreates;
  vacatures = opts.vacature ? [opts.vacature] : [];
  vacatures.forEach(v => lastKnown.set(v.id, v));
}

export function endPractice(): void {
  active = false;
  captureCreates = false;
  vacatures = [];
}

/** A fresh Oefenvacature for the given partner/vestiging. */
export function buildPracticeVacature(partnerId: number | null, branchId: number | null, name: string): Vacature {
  const created = new Date(Date.now() - 3 * 86400000).toISOString();
  return {
    id: PRACTICE_ID,
    functie: "Oefenvacature",
    partner_id: partnerId,
    branch_id: branchId,
    status: "open",
    priority: "normaal",
    contactpersoon: name,
    contactpersoon_picture_url: null,
    hiring_manager: "",
    department: null,
    locatie: "",
    ervaring: "",
    disciplines: [],
    aantal_posities: 1,
    bemiddeling_status: "geen",
    opmerkingen: "Een oefenvacature voor de rondleiding. Wat je hier aanpast, wordt na afloop weer verwijderd.",
    salary_type: null,
    salary_min: null,
    salary_max: null,
    salary_fixed: null,
    salary_other: null,
    created_by: name,
    created_at: created,
    updated_at: created,
    reviewed_at: null,
  };
}

function matches(v: Vacature, params: { status?: string; priority?: string; q?: string }): boolean {
  if (params.status && v.status !== params.status) return false;
  if (params.priority && v.priority !== params.priority) return false;
  if (params.q && !v.functie.toLowerCase().includes(params.q.toLowerCase())) return false;
  return true;
}

/** Puts the practice vacatures matching `params` in front of a first page of /vacatures results. */
export function withPracticeRows(
  res: PagedResponse<Vacature>,
  params: { status?: string; priority?: string; q?: string; ids?: number[]; page?: number },
): PagedResponse<Vacature> {
  if (!active || (params.ids && params.ids.length > 0) || (params.page ?? 1) > 1) return res;
  const rows = vacatures.filter(v => matches(v, params));
  if (rows.length === 0) return res;
  return { ...res, data: [...rows.map(v => ({ ...v })), ...res.data], total: res.total + rows.length };
}

export function getPracticeVacature(id: number): Vacature {
  const v = vacatures.find(x => x.id === id);
  if (!v) throw new Error("Deze oefenvacature bestaat niet meer.");
  return { ...v };
}

export function updatePracticeVacature(id: number, body: Partial<Vacature>): Vacature {
  const i = vacatures.findIndex(x => x.id === id);
  if (i < 0) return { ...(lastKnown.get(id) ?? buildPracticeVacature(null, null, "")) };
  const now = new Date().toISOString();
  const next: Vacature = { ...vacatures[i], ...body, updated_at: now };
  if (body.reviewed_at) next.reviewed_at = now;
  vacatures = vacatures.map((v, k) => (k === i ? next : v));
  lastKnown.set(id, next);
  return { ...next };
}

export function deletePracticeVacature(id: number): void {
  vacatures = vacatures.filter(v => v.id !== id);
}

export function createPracticeVacature(body: Partial<Vacature>, createdBy: string): Vacature {
  const now = new Date().toISOString();
  const v: Vacature = {
    ...buildPracticeVacature(body.partner_id ?? null, body.branch_id ?? null, createdBy),
    ...body,
    id: nextCreatedId--,
    status: "open",
    bemiddeling_status: "geen",
    created_at: now,
    updated_at: now,
  } as Vacature;
  vacatures = [v, ...vacatures];
  lastKnown.set(v.id, v);
  return { ...v };
}
